"use client";

import React, { useEffect, useId, useMemo, useState } from "react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import "../styles/Work.scss";

const projects = [
  {
    title: "Snoutmates",
    category: "SaaS",
    image: "/snoutmates.webp",
    role: "Founder/Developer",
    description:
      "Designed and built a full subscription SaaS platform with Next.js, React, Supabase, Stripe, and Vercel to connect dog owners for playdates and socialization.",
    link: "https://snoutmates.vercel.app",
    tags: [
      "HTML5",
      "CSS3",
      "React",
      "Typescript",
      "Supabase",
      "Stripe",
      "Vercel",
      "Resend",
    ],
  },
];

const work = [
  {
    title: "Solutions Hub: Filter & Search solutions",
    category: "Oracle - Solutions",
    image: "/solutions-hub.avif",
    role: "Lead Developer",
    description:
      "Developed a custom web component for faceted search and dynamic filtering.",
    link: "https://www.oracle.com/cloud/solutions/#search-oci-solutions",
    tags: ["Web Component", "HTML5", "CSS3", "JavaScript", "API"],
  },
  {
    title: "Active Country Selector",
    category: "Oracle - Country Selector",
    image: "/country-selector.avif",
    role: "Lead Developer",
    description:
      "Created a geo-aware selector using browser locale and IP-based detection.",
    link: "https://www.oracle.com/jp/",
    tags: ["HTML5", "CSS3", "JavaScript", "Geo Location", "JSON"],
  },
  {
    title: "Enabling filtered contracts discovery",
    category: "Oracle - Contracts",
    role: "Lead Developer",
    image: "/contracts-filtered.avif",
    description:
      "Improved UX for finding and filtering large contract datasets efficiently.",
    link: "https://www.oracle.com/contracts/cloud-services/",
    tags: ["HTML5", "CSS3", "JavaScript", "API"],
  },
  {
    title: "Optimizing sorted contracts discovery",
    category: "Oracle - Contracts",
    image: "/contracts-sorted.avif",
    role: "Lead Developer",
    description: "Enhanced contract sorting logic and accessibility for global users.",
    link: "https://www.oracle.com/contracts/a-z/contract-documents/",
    tags: ["HTML5", "CSS3", "JavaScript", "API"],
  },
];

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

export default function Work() {
  const groupId = useId();

  const items = useMemo(() => {
    const w = work.map((x) => ({ ...x, kind: "work" }));
    const p = projects.map((x) => ({ ...x, kind: "project" }));
    return [...w,...p];
  }, []);

  const [active, setActive] = useState(null);

  // Escape closes modal
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") setActive(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <section id="work" className="wrapper work-gallery">
      <h2 className="section-title" id="work-title">
        Recent Work & Projects
      </h2>

      <LayoutGroup id={groupId}>
        <div className="bento-grid">
          {items.map((item, idx) => {
            const key = `${item.kind}-${item.title}`;

            return (
              <motion.button
                key={key}
                type="button"
                className={cx("bento-card", `bento-${idx % 7}`)}
                onClick={() => setActive(item)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                layoutId={`card-${key}`}
              >
                <div className="bento-card-content">
                  <motion.div className="bento-media" layoutId={`media-${key}`}>
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                    />
                  </motion.div>

                  <motion.div className="bento-meta" layoutId={`meta-${key}`}>
                    <div className="bento-topline">
                      <span className="bento-kind">
                        {item.kind === "work" ? "Work" : "Project"}
                      </span>
                      <span className="bento-category">{item.category}</span>
                    </div>

                    <h3 className="bento-title">{item.title}</h3>
                    <div className="bento-role">{item.role}</div>
                  </motion.div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence>
          {active && (
            <motion.div
              className="bento-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActive(null)}
            >
              <motion.div
                className="bento-sheet"
                onClick={(e) => e.stopPropagation()}
                initial={{ y: 24, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 24, opacity: 0, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={0.12}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 120 || info.velocity.y > 900) {
                    setActive(null);
                  }
                }}
              >
                <motion.div
                  className="sheet-card"
                  layoutId={`card-${active.kind}-${active.title}`}
                >
                  <div className="sheet-card-content">
                    <motion.div
                      className="sheet-media"
                      layoutId={`media-${active.kind}-${active.title}`}
                    >
                      <img src={active.image} alt={active.title} />
                    </motion.div>

                    <div className="sheet-content">
                      <motion.div
                        className="sheet-meta"
                        layoutId={`meta-${active.kind}-${active.title}`}
                      >
                        <div className="sheet-topline">
                          <span className="sheet-kind">
                            {active.kind === "work" ? "Work" : "Project"}
                          </span>
                          <span className="sheet-category">
                            {active.category}
                          </span>
                        </div>

                        <h3 className="sheet-title">{active.title}</h3>
                        <div className="sheet-role">{active.role}</div>
                      </motion.div>

                      <p className="sheet-description">{active.description}</p>

                      <div className="sheet-tags">
                        {active.tags.map((t) => (
                          <span key={t} className="sheet-tag">
                            {t}
                          </span>
                        ))}
                      </div>

                      <div className="sheet-actions">
                        <a
                          className="sheet-primary"
                          href={active.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Project
                        </a>

                        <button
                          type="button"
                          className="sheet-close"
                          onClick={() => setActive(null)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </LayoutGroup>
    </section>
  );
}
